import React, { useEffect, useRef, useState } from "react";

type UseConfirmButton = {
  isSwapped: boolean;
  onConfirm?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onSwap?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  width: number | undefined;
};

type UseConfirmButtonProps = {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  delay?: number;
  buttonContainerRef: React.RefObject<HTMLDivElement | null>;
};

export const useConfirmButton = ({
  onClick,
  delay = 500,
  buttonContainerRef,
}: UseConfirmButtonProps): UseConfirmButton => {
  const [isButtonSwapped, setIsButtonSwapped] = useState(false);
  const [width, setWidth] = useState(0);

  const timerPointer = useRef<NodeJS.Timeout>(undefined);

  // this will ensure the buttons maintain equal width to avoid jank
  useEffect(() => {
    if (buttonContainerRef.current) {
      setWidth(buttonContainerRef.current.offsetWidth);
    }
  }, [buttonContainerRef]);

  useEffect(() => {
    // this makes sure the reference we use is consistent in set up and tear down
    const currentConfirmButtonRef = buttonContainerRef.current;

    const onMouseLeave = () => {
      timerPointer.current = setTimeout(() => {
        setIsButtonSwapped(false);
      }, delay);
    };

    const onMouseEnter = () => {
      if (timerPointer) clearTimeout(timerPointer.current);
    };

    if (isButtonSwapped && currentConfirmButtonRef)
      currentConfirmButtonRef.addEventListener("mouseleave", onMouseLeave);

    if (isButtonSwapped && currentConfirmButtonRef)
      currentConfirmButtonRef.addEventListener("mouseenter", onMouseEnter);

    return () => {
      if (currentConfirmButtonRef) {
        currentConfirmButtonRef.removeEventListener("mouseleave", onMouseLeave);
        currentConfirmButtonRef.removeEventListener("mouseenter", onMouseEnter);
      }
    };
  }, [isButtonSwapped, delay, buttonContainerRef]);

  const handleChildButtonClick = () => {
    setIsButtonSwapped(true);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isButtonSwapped) {
      setIsButtonSwapped(true);
    } else if (onClick) onClick(event);
  };

  return {
    isSwapped: isButtonSwapped,
    onConfirm: handleClick,
    onSwap: handleChildButtonClick,
    width,
  };
};
